import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import classNames from 'classnames';
import Button from 'material-ui/Button';
import { CircularProgress } from "material-ui/Progress";
import Typography from "material-ui/Typography";
import { isFreePlan } from '../../../utils/planUtils';
import { isEnableLetterAI } from '../../../utils/userOptionUtils';
import LetterGenreControl from '../controls/LetterGenreControl';
import KeywordsControl from '../controls/KeywordsControl';
import { letterGenreValues, initialKeywords } from '../../../constants/LetterAIValues';

const { OrderedMap } = require('immutable');

const styles = () => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    background: '#FFFFFF 0% 0% no-repeat padding-box',
    backgroundColor: '#FFFFFF',
    '-webkit-box-shadow': '0px 0px 3px 0px rgba(0,0,0,0.15)',
    '-moz-box-shadow': '0px 0px 3px 0px rgba(0,0,0,0.15)',
    boxShadow: '0px 0px 3px 0px rgba(0,0,0,0.15)',
    padding: "60px 40px",
    width: "100%",
  },

  img: {
    width: '108px',
    height: '108px',
  },

  title: {
    width: '100%',
    textAlign: 'center',
    color: '#5090FF',
    fontSize: '16px',
    fontWeight: 'bold',
  },

  description: {
    fontSize: "11px",
    width: "100%",
    marginTop: "20px",
  },

  btn: {
    marginTop: '40px',
    padding: '15px 0',
    width: "100%",
    borderRadius: "2px",
    fontSize: "16px",
  },

  btnDisable: {
    border: "1px solid #ECEDEF",
    background: "#ECEDEF",
    color: "#000000",
  },

  btnEnable: {
    border: "1px solid #1A65FF",
    background: "#FFFFFF",
    color: "#1A65FF",
    "&:hover": {
      background: "#1A65FF",
      color: '#FFFFFF',
    },
  },

  resultWrapper: {
    width: "100%",
  },

  result: {
    marginTop: '40px',
    border: "1px solid #D9DBDF",
    width: "100%",
  },

  resultContent: {
    fontSize: "11px",
    lineHeight: "1.5rem",
    padding: "20px",
    whiteSpace: "pre-wrap",
  },
});

class LetterAutoGeneratePanel extends React.Component {
  initialState = {
    selected_genre: letterGenreValues[0].value.toString(),
    keywords: OrderedMap(initialKeywords),
    progress: 0,
    progressIntervalId: null,
  }


  constructor(props) {
    super(props);
    this.state = { ...this.initialState };
  }

  componentDidUpdate(prevProps) {
    const aboutMaxPercent = 80;
    const maxPercent = 100;
    const randomInt = 3;
    const interval = 1000;
    const addRandomInt = 8;
    const baseInt = 4;
    const finInterval = 800;
    const successInterval = 100;
    // 自動生成前 -> 自動生成中になった場合
    if ((prevProps.isGenerating === false && this.props.isGenerating === true)) {
      // プログレスの値を0にする
      this.setState({
        progress: 0,
      }, () => {
        // 1秒ごとにランダムで0~3秒プログレスの値を加算するタイマー 最大 79 + (0 or 1 or 2 or 3)
        const progressIntervalId = setInterval(() => {
          this.setState({
            progress: this.state.progress + ((this.state.progress < aboutMaxPercent) ? (Math.floor(Math.random() * randomInt)) : 0),
          });
        }, interval);
        // 処理終了後にステートに消せるようにstate入れておく
        this.setState({
          progressIntervalId: progressIntervalId,
        });
      });
    }

    // 自動生成中 -> 自動生成終了になった場合
    if ((prevProps.isGenerating === true && this.props.isGenerating === false)) {
      // タイマーをクリアする
      clearInterval(this.state.progressIntervalId);

      // 添削成功した場合
      if (this.hasGeneratedContents()) {
        // 100%までの残りの%を緩やかに100%にさせる
        const progressRemainingInterval = setInterval(() => {
          let remainingPercent = maxPercent - this.state.progress;

          // 0.1秒ごとにランダムで4~12秒プログレスの値を加算する
          let rand = Math.floor(Math.random() * addRandomInt) + baseInt;
          // 乱数が残りの時間が超える場合はそのまま残りの時間を乱数とする 例: 残り3% 乱数5 => 3
          if (rand > remainingPercent) {
            rand = remainingPercent;
          }

          this.setState({
            progress: this.state.progress + rand,
          }, () => {
            // 100%であれば終了処理のタイマーを0.8秒余韻を残してクリア
            if (this.state.progress === maxPercent) {
              clearInterval(progressRemainingInterval);
              setTimeout(() => {
                this.setState({
                  progressIntervalId: null,
                });
              }, finInterval);
            }
          });
        }, successInterval);
      } else {
        // 失敗した場合は即時にタイマーをstateから外す
        this.setState({
          progressIntervalId: null,
        });
      }
    }
  }

  handleChange = (property, index = null) => e => {
    if (index !== null) {
      const updatedMap = this.state[property].set(index, e.target.value);
      this.setState({
        [property]: updatedMap,
      });
    } else {
      this.setState({
        [property]: e.target.value,
      });
    }
  }

  handleAddMapItem = (property) => {
    const updatedMap = this.state[property].set(this.state[property].size, "");
    this.setState({
      [property]: updatedMap,
    });
  }

  isEditDisable = () => {
    return this.props.isGenerating || this.state.progressIntervalId;
  }

  hasGeneratedContents = () => {
    return this.props.generatedContents &&
      this.props.generatedContents !== "";
  }

  isGenerateRequestDisable = () => {
    return this.isEditDisable() ||
      this.state.selected_genre === this.initialState.selected_genre ||
      this.state.keywords.every((value) => value === '');
  }

  isDisplayResult = () => {
    return this.hasGeneratedContents() && !this.isEditDisable();
  }

  handleSubmit = () => {
    if (isFreePlan(this.props.user)) {
      this.props.togglePlanDialog();
    } else if (!isEnableLetterAI(this.props.user)) {
      this.props.togglePromotePlanChangeDialog();
    } else {
      // 重複と空文字を削除 (Setで重複削除, filterで空文字削除)し、配列にフォーマット
      const keywords = Array.from(
        new Set(this.state.keywords
          .filter(value => value !== '')
          .valueSeq()
          .toArray()
        )
      );

      this.props.postGenerate({
        "selected_genre": this.state.selected_genre,
        "keywords": keywords,
      });
    }
  }

  render() {
    const { classes } = this.props;
    const disable = this.isEditDisable();

    return (
      <div className={classes.root}>
        <img
          src={require(`../../../assets/images/letter-auto-generate.png`)}
          alt="文章添削"
          className={classes.img}
        />
        <div className={classes.title} style={{ marginTop: '30px' }}>
          レター文面のサンプルを自動で生成
        </div>
        <LetterGenreControl
          style={{ marginTop: '60px' }}
          selectedGenre={this.state.selected_genre}
          handleChange={(e, property) => this.handleChange(property)(e)}
          disabled={disable}
        />
        <KeywordsControl
          style={{ marginTop: '45px' }}
          disabled={disable}
          keywords={this.state.keywords}
          handleChange={(e, property, index) => this.handleChange(property, index)(e)}
          handleAddMapItem={(property) => this.handleAddMapItem(property)}
        />

        <Button
          className={classNames(classes.btn, this.isGenerateRequestDisable() ? classes.btnDisable : classes.btnEnable)}
          onClick={this.handleSubmit}
          disabled={this.isGenerateRequestDisable()}
        >
          {(disable) ?
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span>
                {`生成中です (${this.state.progress}%)`}
              </span>
              <div style={{ position: "relative", display: "flex", alignItems: "center", marginLeft: "10px" }}>
                <CircularProgress
                  style={{ color: "#6F9EFF", height: "18px", width: "18px", position: "relative" }}
                />
              </div>
            </div>
            :
            <span>{"生成"}</span>
          }
        </Button>
        {
          this.isDisplayResult() &&
          (
            <div className={classes.resultWrapper}>
              <div className={classes.result}>
                <div className={classes.resultContent}>{this.props.generatedContents}</div>
              </div>
              {/* <Button className={classNames(classes.btn, classes.btnEnable)}>Wordファイル(.docx)をダウンロード</Button> */}
              <Typography
                variant="body2"
                className={classes.description}
              >
                <div>
                  ※自動生成したレターの文面は、商談率の高い文面を約束するものではありません。Wordにて文面の内容をご確認・編集する等、レター作成の補助機能としてご利用ください。
                </div>
              </Typography>
            </div>
          )
        }
      </div>
    );
  }
}

LetterAutoGeneratePanel.propTypes = {
  classes: PropTypes.object,
  user: PropTypes.object,
  isGenerating: PropTypes.bool,
  postCorrect: PropTypes.func,
  togglePlanDialog: PropTypes.func.isRequired,
  togglePromotePlanChangeDialog: PropTypes.func.isRequired,
  generatedContents: PropTypes.string,
  postGenerate: PropTypes.func.isRequired,
};

export default withStyles(styles)(LetterAutoGeneratePanel);



// WEBPACK FOOTER //
// ./src/components/LetterAutoGenerate/panels/LetterAutoGeneratePanel.jsx